// tslint:disable:max-file-line-count
import { IBookingReceipt } from '@travelnest/typewriter';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import api from '../../../../../api/index';
import { ILatestVerificationState } from '../../../../../data/entities/booking-state';
import { ICommissionBreakdownObject } from '../../../../../data/entities/commission-breakdown';
import { ICustomer } from '../../../../../data/entities/customer';
import { IDirectDiscounts } from '../../../../../data/entities/direct-discounts';
import { IEventV2 } from '../../../../../data/entities/event';
import { IEventNote, IPendingEventNote } from '../../../../../data/entities/event/event-note';
import { IPendingPostPayment } from '../../../../../data/entities/post-payment';
import { collapsePending } from '../../../../../data/entities/post-payment';
import { IPromotionOpportunity } from '../../../../../data/entities/promotions';
import { IProperty } from '../../../../../data/entities/property';
import { actions } from '../../../../../data/store';
import { isAdmin } from '../../../../../entities/admin/selectors';
import {
	cancelBooking,
	getBookingById,
	getFirstBookingIdByOwnerId,
	getRUBookingCurrency, requestBookingPayment, updateBooking
} from '../../../../../entities/bookings/actions';
import { getCommissionBreakdown } from '../../../../../entities/commission-breakdown/actions';
import { createEventNotes, deleteEventNote } from '../../../../../entities/event-notes/actions';
import { collapsePending as collapsePendingEventNotes } from '../../../../../entities/event-notes/index';
import { getFXRateByEvent } from '../../../../../entities/fx/actions';
import { loadLatestVerification } from '../../../../../entities/latest-verification-state/helpers';
import { createPostPayment, deletePostPayment } from '../../../../../entities/post-payment/actions';
import { fromFloyd } from '../../../../../entities/pricing/helpers';
import { getProperty } from '../../../../../entities/properties/selectors';
import { IThunkDispatch } from '../../../../../store/middleware';
import { IState } from '../../../../../store/reducer';
import { useDataLoad } from '../../../../../view/helpers/hooks';
import { PropertyBookingsComponent } from './PropertyBooking';

const mapDispatchToProps = (dispatch: IThunkDispatch) => ({
	getFirstBookingId: (customerId: string) => dispatch(getFirstBookingIdByOwnerId(customerId)),
	getBookingData: (propertyId: string, eventId: string) => dispatch(getBookingById(propertyId, eventId)).catch((e: any) => e),
	getPropertyData: (id: string) => dispatch(actions.property.getById(id)).catch((e: any) => e),
	getCustomerData: (id: string) => dispatch(actions.customer.getById(id)).catch((e: any) => e),
	getCommissionBreakdownData: (propertyId: string, channelIds: string[]) =>
		dispatch(getCommissionBreakdown(propertyId, channelIds)).catch((e: any) => e),
	cancelBooking: (propertyId: string, eventId: string) => dispatch(cancelBooking(propertyId, eventId)),
	updateBooking: (propertyId: string, eventId: string, event: IEventV2) => dispatch(updateBooking(propertyId, eventId, event)),
	getBookingStateEvents: (propertyId: string, eventId: string) =>
		dispatch(actions.bookingState.getStateEventStreamByEvent(propertyId, eventId)),
	submitOverrides: (propertyId: string, eventId: string, overrides: string[]) =>
		dispatch(actions.bookingState.submitOverrides(propertyId, eventId, overrides)),
	auditUpdates: async (propertyId: string, eventId: string, event: IEventV2) => {
		// List of fields for which we want to track changes
		const auditableFields = ['owner_rent_value', 'cash_receipt_value', 'start_on', 'end_on'];

		const user = (await api.authentication.getCurrentUser()).getUsername();

		const auditNotes: IEventNote[] = [];

		auditableFields.forEach((fieldName) => {
			const oldValue: string | null = sessionStorage.getItem('old_' + fieldName);
			const newValue: string | null = sessionStorage.getItem('new_' + fieldName);

			if ((oldValue !== null) && (oldValue !== newValue)) {
				const text: string = user + ' changed ' + fieldName + ' from ' + oldValue + ' to ' + newValue;
				auditNotes.push({
					event_id: eventId,
					note_type: 'audit',
					content: text
				} as IEventNote);
			}

			sessionStorage.removeItem('old_' + fieldName);
			sessionStorage.removeItem('new_' + fieldName);
		});

		const auditableReceipts = ['owner_payments', 'guest_payments'];

		auditableReceipts.forEach((fieldName) => {
			const removedValue: string | null = sessionStorage.getItem('deleted_' + fieldName);
			const addedValue: string | null = sessionStorage.getItem('added_' + fieldName);

			if ((removedValue !== null)) {
				const receipts: [IBookingReceipt] = JSON.parse(removedValue);
				receipts.forEach(({ date, currency, amount, method, reference }) => {
					const text = user + ' removed ' + fieldName + ' date: ' + date + ' currency: ' + currency + ' amount: ' + amount + ' method: ' + method + ' ref: ' + reference;
					auditNotes.push({
						event_id: eventId,
						note_type: 'audit',
						content: text
					} as IEventNote);
				});

			}

			if ((addedValue !== null)) {
				const receipts: [IBookingReceipt] = JSON.parse(addedValue);
				receipts.forEach((receipt) => {
					const text: string = user + ' added ' + fieldName + ' date: ' + receipt.date + ' currency: ' + receipt.currency + ' amount: ' + receipt.amount + ' method: ' + receipt.method + ' ref: ' + receipt.reference;
					auditNotes.push({
						event_id: eventId,
						note_type: 'audit',
						content: text
					} as IEventNote);
				});

			}

			sessionStorage.removeItem('deleted_' + fieldName);
			sessionStorage.removeItem('added_' + fieldName);
		});

		if (auditNotes.length > 0) {
			await dispatch(createEventNotes(
				propertyId,
				eventId,
				auditNotes
			));
		}
	},
	submitPostPayments: async (propertyId: string, eventId: string, postPayments: IPendingPostPayment[]) => {
		const flattenedPostPayments = collapsePending(postPayments);
		const added = flattenedPostPayments.filter((payment) => payment.action === 'add');
		const deleted = flattenedPostPayments.filter((payment) => payment.action === 'remove');

		const user = (await api.authentication.getCurrentUser()).getUsername();

		if (added.length > 0) {
			await dispatch(createEventNotes(
				propertyId,
				eventId,
				added.map(({ payment: { created_at, currency, amount, reason } }) => {
					const text = user + ' added exceptional payment date: ' + created_at +
						' currency: ' + currency + ' amount: ' + fromFloyd(amount) + ' ref: ' + reason;
					return {
						event_id: eventId,
						note_type: 'audit',
						content: text
					};
				})
			));
			await dispatch(createPostPayment(
				added.map((payment) => payment.payment),
				propertyId,
				eventId
			));
		}

		if (deleted.length > 0) {
			await dispatch(createEventNotes(
				propertyId,
				eventId,
				deleted.map(({ payment: { created_at, currency, amount, reason } }) => {
					const text = user + ' removed exceptional payment date: ' + created_at
						+ ' currency: ' + currency + ' amount: ' + fromFloyd(amount) + ' ref: ' + reason;
					return {
						event_id: eventId,
						note_type: 'audit',
						content: text
					};
				})
			));
			for (const payment of deleted) {
				await dispatch(deletePostPayment(payment.payment, propertyId, eventId));
			}
		}
	},
	submitEventNotes: async (propertyId: string, eventId: string, eventNotes: IPendingEventNote[]) => {
		const flattenedEventNotes = collapsePendingEventNotes(eventNotes);
		const added = flattenedEventNotes.filter((note) => note.action === 'add');
		const deleted = flattenedEventNotes.filter((note) => note.action === 'remove');

		if (added.length > 0) {
			await dispatch(createEventNotes(
				propertyId,
				eventId,
				added.map((note) => note.eventNote)
			));
		}

		if (deleted.length > 0) {
			for (const note of deleted) {
				await dispatch(deleteEventNote(propertyId, eventId, note.eventNote as IEventNote));
			}
		}
	},
	getFXRates: (event: IEventV2) => dispatch(getFXRateByEvent(event)),
	getRUCurrency: (propertyId: string, eventId: string, rentalsunitedBookingId: string, currency?: string) =>
		dispatch(getRUBookingCurrency(propertyId, eventId, rentalsunitedBookingId, currency)),
	requestBookingPayment: (propertyId: string, eventId: string) =>
		dispatch(requestBookingPayment(propertyId, eventId)),
});

const mapStateToProps = (propertyId: string | number, bookingId: string) => (state: IState) => {
	const property = getProperty(propertyId, state);
	const promotionOpportunities = state.data.promotionOpportunities;
	const event = state.entities.bookings.byId[bookingId];
	const customerId = property ? property.owner_id : '';
	const commissionBreakdown = state.entities.commission.byPropertyId[propertyId];
	const directDiscounts = state.entities.directDiscounts.byPropertyId[propertyId];
	const firstBookingId = state.entities.bookings.firstBookingIdbyOwnerId[customerId];
	const cancelling = state.views.property.bookings.list.cancellingBooking;
	const saving = state.views.property.bookings.details.saving;
	const admin = isAdmin(state);
	const customer = state.data.customers.byId[customerId];
	const pendingPostPayments = state.entities.pendingPostPayments.pendingPostPaymentData;
	const fxRates = state.views.property.bookings.details.fxRatesByEvent[bookingId];
	const pendingEventNotes = state.entities.pendingEventNotes.pendingEventNoteData;
	const bookingStateEvents = state.data.bookingState.byEventId[bookingId];
	const submitOverridesSaveState = state.views.property.bookings.details.automationProcess.submitOverridesSaveState;

	return {
		bookingId,
		propertyId,
		property,
		event,
		firstBookingId,
		cancelling,
		admin,
		customerId,
		customer,
		commissionBreakdown,
		directDiscounts,
		saving,
		pendingPostPayments,
		fxRates,
		pendingEventNotes,
		bookingStateEvents,
		submitOverridesSaveState,
		promotionOpportunities
	};
};

export const PropertyBookings: React.FC = () => {
	const params = useParams();
	const bookingId = params.booking_id || '';
	const propertyId = params.property_id || '';
	const state = useSelector(mapStateToProps(propertyId, bookingId));
	const disp = mapDispatchToProps(useDispatch());

	const loadingProperty = useDataLoad(
		!state.property,
		() => disp.getPropertyData(propertyId).catch((e: any) => e));

	const loadingEvent = useDataLoad(
		!state.event,
		async () => await disp.getBookingData(propertyId, bookingId).catch((e: any) => e));

	const channelIds = ['booking.com'];
	const loadingCommissionBreakdown = useDataLoad(
		!state.commissionBreakdown,
		() => disp.getCommissionBreakdownData(propertyId, channelIds).catch((e: any) => e));

	const loadingCustomer = useDataLoad(
		!!state.property && !state.customer, // Need to wait for property so we can get the owner ID from it
		() => disp.getCustomerData(state.customerId).catch((e: any) => e));

	let loadingFirstBooking = false;
	let loadingFxRates = false;
	let loadingBookingState = false;
	let loadingLatestVerificationState = false;
	let latestVerificationState = {} as ILatestVerificationState;

	if (state.admin) {
		loadingFirstBooking = useDataLoad(
			!!state.customerId && !state.firstBookingId,
			() => disp.getFirstBookingId(state.customerId).catch((e: any) => e));

		loadingFxRates = useDataLoad(
			!!state.event && !state.fxRates,
			() => disp.getFXRates(state.event!).catch((e: any) => e));

		loadingBookingState = useDataLoad(
			!state.bookingStateEvents,
			() => disp.getBookingStateEvents(propertyId, bookingId).catch((e: any) => e));

		const { loadingLatestVerification, latestVerification } = loadLatestVerification(propertyId, bookingId);

		loadingLatestVerificationState = loadingLatestVerification;
		latestVerificationState = latestVerification || {} as ILatestVerificationState;
	}

	return (
		<PropertyBookingsComponent
			property={state.property || {} as IProperty}
			event={state.event || {} as IEventV2}
			promotionOpportunities={state.promotionOpportunities || [] as IPromotionOpportunity[]}
			commissionBreakdown={state.commissionBreakdown as ICommissionBreakdownObject}
			directDiscounts={state.directDiscounts as IDirectDiscounts[]}
			firstBooking={state.firstBookingId === bookingId}
			customer={state.customer || {} as ICustomer}
			loading={
				loadingCustomer || loadingProperty ||
				loadingEvent || loadingFirstBooking ||
				loadingFxRates ||
				loadingBookingState || loadingLatestVerificationState
				|| loadingCommissionBreakdown
			}
			saving={state.saving}
			admin={state.admin}
			cancelBooking={disp.cancelBooking}
			cancelling={state.cancelling}
			updateBooking={disp.updateBooking}
			submitPostPayments={disp.submitPostPayments}
			pendingPostPayments={state.pendingPostPayments}
			bookingStateEvents={state.bookingStateEvents}
			fxRates={state.fxRates || {}}
			auditUpdates={disp.auditUpdates}
			submitEventNotes={disp.submitEventNotes}
			pendingEventNotes={state.pendingEventNotes}
			getRUCurrency={disp.getRUCurrency}
			requestBookingPayment={disp.requestBookingPayment}
			latestVerificationState={latestVerificationState || {} as ILatestVerificationState}
			submitOverridesSaveState={state.submitOverridesSaveState}
			submitOverrides={disp.submitOverrides}
		/>
	);
};
